import { ShipmentStatus } from '#/shared/api/generated/shipment/models'

export const ORDER_MARK_AS_STATUSES = [
  ShipmentStatus.order,
  ShipmentStatus.posted,
  ShipmentStatus.not_signed,
  ShipmentStatus.dispatched,
  ShipmentStatus.issue,
  ShipmentStatus.picked_up,
  ShipmentStatus.completed,
  ShipmentStatus.on_hold,
  ShipmentStatus.archived,
] as const

export type OrderMarkAsStatus = (typeof ORDER_MARK_AS_STATUSES)[number]

const LABEL_KEY: Record<OrderMarkAsStatus, string> = {
  [ShipmentStatus.order]: 'detailToolbar.markAsOptions.order',
  [ShipmentStatus.posted]: 'detailToolbar.markAsOptions.posted',
  [ShipmentStatus.not_signed]: 'detailToolbar.markAsOptions.notSigned',
  [ShipmentStatus.dispatched]: 'detailToolbar.markAsOptions.dispatched',
  [ShipmentStatus.issue]: 'detailToolbar.markAsOptions.issue',
  [ShipmentStatus.picked_up]: 'detailToolbar.markAsOptions.pickedUp',
  [ShipmentStatus.completed]: 'detailToolbar.markAsOptions.completed',
  [ShipmentStatus.on_hold]: 'detailToolbar.markAsOptions.onHold',
  [ShipmentStatus.archived]: 'detailToolbar.markAsOptions.archived',
}

export function orderMarkAsLabelKey(status: OrderMarkAsStatus): string {
  return LABEL_KEY[status]
}

const DISPATCHED_STATUSES = new Set<string>([
  ShipmentStatus.not_signed,
  ShipmentStatus.dispatched,
  ShipmentStatus.picked_up,
  ShipmentStatus.completed,
])

/**
 * A carrier has been dispatched (booked) on the order — distinguishes the
 * loadboard "dispatched" state from a plain "posted" listing. Per the
 * single-assignment invariant, only the dispatched board's listing survives.
 * `not_signed` is the Central Dispatch post-dispatch / pre-contract state, so
 * it shows Cancel Order rather than Repost/Remove (DEV-661).
 */
export function isDispatchedStatus(status: string): boolean {
  return DISPATCHED_STATUSES.has(status)
}

const SD_ORDER_EDITABLE_STATUSES = new Set<string>([
  ShipmentStatus.not_signed,
  ShipmentStatus.dispatched,
  ShipmentStatus.picked_up,
])

/**
 * The Super Dispatch order is live and still in flight — it can be updated in
 * place (repost) or its offer cancelled (DEV-756). Covers the pre-signature
 * window plus `picked_up`, which keeps the same kebab actions instead of
 * Cancel Order.
 */
export function isSdOrderEditableStatus(status: string): boolean {
  return SD_ORDER_EDITABLE_STATUSES.has(status)
}

/**
 * Central Dispatch post-dispatch / pre-contract state — the listing can still
 * be reposted to find another carrier, but Remove is posted-only.
 */
export function isCdOrderNotSignedStatus(status: string): boolean {
  return status === ShipmentStatus.not_signed
}
